import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormHelperText,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  AccessTimeOutlined,
  CheckCircleOutlineRounded,
  ShieldOutlined,
  StorefrontOutlined,
  VpnKeyOutlined,
} from "@mui/icons-material";
import { alpha, useTheme } from "@mui/material/styles";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { useAuth } from "../../context/AuthContext";
import { authService } from "../../services";
import fallbackLogo from "../../assets/pawsi.jpeg";

const BRAND_NAME = "Pawsi Patitas";

const reasonMeta = {
  not_found: {
    title: "Link no encontrado",
    description: "Este enlace no existe o ya no esta disponible en el sistema.",
  },
  expired: {
    title: "Este link ha expirado",
    description: "El tiempo disponible termino y la invitacion quedo inactiva.",
  },
  used: {
    title: "Este link ya fue utilizado",
    description: "La invitacion ya se consumio y no puede reutilizarse para otra cuenta.",
  },
  canceled: {
    title: "Este link fue cancelado",
    description: "La invitacion fue invalidada por administracion y ya no puede utilizarse.",
  },
};

const getPrimaryInviteBranch = (inviteData) => {
  const branches = Array.isArray(inviteData?.branches) ? inviteData.branches : [];
  return branches.find((item) => item?.current) || branches[0] || null;
};

const formatDateTime = (value, options) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("es-GT", options).format(date);
};

const formatShortDateTime = (value) =>
  formatDateTime(value, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const formatExactDateTime = (value) =>
  formatDateTime(value, {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const getPasswordStrength = (password) => {
  const raw = String(password || "");
  if (!raw) return 0;

  let score = Math.min(raw.length * 8, 40);
  if (/[a-z]/.test(raw)) score += 15;
  if (/[A-Z]/.test(raw)) score += 15;
  if (/\d/.test(raw)) score += 15;
  if (/[^A-Za-z0-9]/.test(raw)) score += 15;
  return Math.max(0, Math.min(score, 100));
};

const getStrengthColor = (score) => {
  if (score < 40) return "#E24B4A";
  if (score < 70) return "#BA7517";
  return "#1D9E75";
};

const extractErrorMessage = (error) => {
  const data = error?.response?.data;
  if (!data || typeof data !== "object") {
    return "No se pudo crear la cuenta con esta invitacion.";
  }
  if (typeof data.detail === "string" && data.detail.trim()) return data.detail;
  const firstKey = Object.keys(data)[0];
  const firstValue = data[firstKey];
  if (Array.isArray(firstValue) && firstValue[0]) return String(firstValue[0]);
  if (typeof firstValue === "string" && firstValue.trim()) return firstValue;
  return "No se pudo crear la cuenta con esta invitacion.";
};

const getCountdownParts = (expiresAt, nowValue) => {
  const target = new Date(expiresAt).getTime();
  if (!target || Number.isNaN(target)) {
    return [
      { label: "Dias", value: "00" },
      { label: "Horas", value: "00" },
      { label: "Min", value: "00" },
      { label: "Seg", value: "00" },
    ];
  }

  let remaining = Math.max(0, target - nowValue);
  const day = Math.floor(remaining / 86400000);
  remaining -= day * 86400000;
  const hour = Math.floor(remaining / 3600000);
  remaining -= hour * 3600000;
  const minute = Math.floor(remaining / 60000);
  remaining -= minute * 60000;
  const second = Math.floor(remaining / 1000);

  return [
    { label: "Dias", value: String(day).padStart(2, "0") },
    { label: "Horas", value: String(hour).padStart(2, "0") },
    { label: "Min", value: String(minute).padStart(2, "0") },
    { label: "Seg", value: String(second).padStart(2, "0") },
  ];
};

function SummaryMetric({ icon: Icon, label, value }) {
  return (
    <Box
      sx={(theme) => ({
        p: 1.45,
        borderRadius: 2.5,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
        bgcolor: alpha(theme.palette.background.paper, 0.72),
        display: "flex",
        gap: 1.1,
        alignItems: "flex-start",
      })}
    >
      <Box
        sx={(theme) => ({
          width: 34,
          height: 34,
          borderRadius: 2,
          bgcolor: alpha(theme.palette.primary.main, 0.1),
          color: theme.palette.primary.main,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        })}
      >
        <Icon sx={{ fontSize: 18 }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{label}</Typography>
        <Typography sx={{ mt: 0.35, fontSize: 13, fontWeight: 500, lineHeight: 1.35 }}>
          {value || "-"}
        </Typography>
      </Box>
    </Box>
  );
}

function CountdownItem({ label, value }) {
  return (
    <Box
      sx={(theme) => ({
        minWidth: 72,
        px: 1.15,
        py: 1.05,
        borderRadius: 2.5,
        border: `1px solid ${alpha(theme.palette.secondary.main, 0.12)}`,
        bgcolor: alpha(theme.palette.background.paper, 0.82),
        textAlign: "center",
      })}
    >
      <Typography sx={{ fontSize: 22, lineHeight: 1.1, fontWeight: 500 }}>
        {value}
      </Typography>
      <Typography sx={{ mt: 0.3, fontSize: 11, color: "text.secondary" }}>{label}</Typography>
    </Box>
  );
}

function InviteAcceptPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { loadMe } = useAuth();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const token = String(searchParams.get("token") || params.token || "").trim();
  const [loading, setLoading] = useState(true);
  const [inviteData, setInviteData] = useState(null);
  const [reason, setReason] = useState("not_found");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dpi, setDpi] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [nowValue, setNowValue] = useState(() => Date.now());

  const primaryBranch = useMemo(() => getPrimaryInviteBranch(inviteData), [inviteData]);
  const passwordStrength = useMemo(() => getPasswordStrength(password), [password]);
  const passwordsMatch = Boolean(password) && Boolean(confirmPassword) && password === confirmPassword;
  const usernamePatternValid = /^[A-Za-z0-9.]+$/.test(username.trim());
  const normalizedDpi = useMemo(() => String(dpi || "").replace(/\D/g, ""), [dpi]);
  const countdownParts = useMemo(
    () => getCountdownParts(inviteData?.expires_at, nowValue),
    [inviteData?.expires_at, nowValue]
  );
  const hasExpiry = Boolean(inviteData?.expires_at);
  const countdownExpired = countdownParts.every((part) => part.value === "00");
  const exactExpiry = useMemo(() => formatExactDateTime(inviteData?.expires_at), [inviteData?.expires_at]);
  const shortExpiry = useMemo(() => formatShortDateTime(inviteData?.expires_at), [inviteData?.expires_at]);
  const canSubmit =
    Boolean(token) &&
    Boolean(firstName.trim()) &&
    Boolean(lastName.trim()) &&
    Boolean(username.trim()) &&
    usernamePatternValid &&
    normalizedDpi.length === 13 &&
    password.length >= 8 &&
    passwordsMatch &&
    !submitting &&
    Boolean(inviteData?.valid);

  useEffect(() => {
    let active = true;

    if (!token) {
      setInviteData(null);
      setReason("not_found");
      setLoading(false);
      return () => {
        active = false;
      };
    }

    setLoading(true);
    setSubmitError("");

    authService
      .validateInvite(token)
      .then((data) => {
        if (!active) return;
        setInviteData(data);
        setReason(data?.reason || "not_found");
      })
      .catch(() => {
        if (!active) return;
        setInviteData(null);
        setReason("not_found");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [token]);

  useEffect(() => {
    if (!inviteData?.expires_at) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setNowValue(Date.now());
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [inviteData?.expires_at]);

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      if (!canSubmit) return;

      setSubmitting(true);
      setSubmitError("");
      try {
        await authService.acceptInvite({
          token,
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          dpi: normalizedDpi,
          username: username.trim(),
          password,
        });
        await loadMe();
        navigate("/", { replace: true });
      } catch (error) {
        setSubmitError(extractErrorMessage(error));
      } finally {
        setSubmitting(false);
      }
    },
    [canSubmit, firstName, lastName, loadMe, navigate, normalizedDpi, password, token, username]
  );

  const invalidMeta = reasonMeta[reason] || reasonMeta.not_found;
  const summaryMetrics = [
    {
      icon: StorefrontOutlined,
      label: "Sucursal asignada",
      value: primaryBranch?.branch?.name || "Sucursal pendiente",
    },
    {
      icon: ShieldOutlined,
      label: "Rol inicial",
      value: primaryBranch?.role?.name || "Sin rol",
    },
    {
      icon: AccessTimeOutlined,
      label: "Expiracion exacta",
      value: exactExpiry,
    },
    {
      icon: VpnKeyOutlined,
      label: "Modalidad",
      value: "Link de acceso de un solo uso",
    },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(180deg, ${theme.palette.background.default} 0%, ${alpha(theme.palette.primary.light, 0.1)} 100%)`,
        px: { xs: 2, md: 3 },
        py: { xs: 3, md: 4 },
      }}
    >
      <Box
        sx={{
          maxWidth: 1160,
          mx: "auto",
          position: "relative",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: -60,
              left: { xs: -30, md: 120 },
              width: 220,
              height: 220,
              borderRadius: "50%",
              bgcolor: alpha(theme.palette.secondary.light, 0.14),
            }}
          />
          <Box
            sx={{
              position: "absolute",
              right: -80,
              bottom: 50,
              width: 260,
              height: 260,
              borderRadius: "50%",
              bgcolor: alpha(theme.palette.primary.light, 0.12),
            }}
          />
        </Box>

        <Paper
          elevation={0}
          sx={{
            position: "relative",
            overflow: "hidden",
            borderRadius: { xs: 3, md: 4 },
            bgcolor: alpha(theme.palette.background.paper, 0.9),
            boxShadow: `0 24px 60px ${alpha(theme.palette.primary.dark, 0.1)}`,
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "0.92fr 1.08fr" },
              minHeight: { md: 720 },
            }}
          >
            <Box
              sx={{
                px: { xs: 2.2, md: 4 },
                py: { xs: 2.5, md: 4 },
                background: `linear-gradient(170deg, ${alpha(theme.palette.primary.main, 0.11)} 0%, ${alpha(theme.palette.secondary.light, 0.12)} 58%, ${alpha(theme.palette.background.paper, 0.92)} 100%)`,
                borderRight: { md: `1px solid ${alpha(theme.palette.primary.main, 0.08)}` },
                display: "flex",
                flexDirection: "column",
                gap: 2.5,
              }}
            >
              <Stack direction="row" spacing={1.4} alignItems="center">
                <Box
                  component="img"
                  src={fallbackLogo}
                  alt={BRAND_NAME}
                  sx={{
                    width: { xs: 52, md: 60 },
                    height: { xs: 52, md: 60 },
                    borderRadius: 2.5,
                    objectFit: "cover",
                    boxShadow: `0 12px 28px ${alpha(theme.palette.primary.dark, 0.18)}`,
                  }}
                />
                <Box>
                  <Typography sx={{ fontSize: 11, color: "text.secondary", letterSpacing: "0.12em", textTransform: "uppercase" }}>
                    Activacion de cuenta
                  </Typography>
                  <Typography sx={{ fontSize: { xs: 22, md: 28 }, fontWeight: 500, lineHeight: 1.1 }}>
                    {BRAND_NAME}
                  </Typography>
                </Box>
              </Stack>

              <Box>
                <Typography sx={{ fontSize: { xs: 24, md: 34 }, fontWeight: 500, lineHeight: 1.12 }}>
                  Una bienvenida clara, segura y lista para empezar.
                </Typography>
                <Typography
                  color="text.secondary"
                  sx={{ mt: 1.4, fontSize: 14, lineHeight: 1.7, maxWidth: 420 }}
                >
                  Completa tus datos personales, define tus credenciales y activa tu acceso
                  operativo sin pasos extra ni procesos externos.
                </Typography>
              </Box>

              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 3,
                  bgcolor: alpha(theme.palette.background.paper, 0.68),
                }}
              >
                <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                  Tiempo restante para activar
                </Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.25 }}>
                  {countdownParts.map((part) => (
                    <CountdownItem key={part.label} label={part.label} value={part.value} />
                  ))}
                </Stack>
                <Typography
                  sx={{
                    mt: 1.25,
                    fontSize: 12,
                    color: hasExpiry && countdownExpired ? "error.main" : "text.secondary",
                  }}
                >
                  {!hasExpiry
                    ? "La expiracion exacta aparecera cuando la invitacion sea validada."
                    : countdownExpired
                      ? "El tiempo visual llego a cero. Si el link ya expiro, el servidor rechazara el alta."
                      : `Expira el ${shortExpiry}`}
                </Typography>
              </Paper>

              <Stack spacing={1.2}>
                <SummaryMetric
                  icon={StorefrontOutlined}
                  label="Sucursal"
                  value={primaryBranch?.branch?.name || "Pendiente por validar"}
                />
                <SummaryMetric
                  icon={ShieldOutlined}
                  label="Rol"
                  value={primaryBranch?.role?.name || "Sin rol"}
                />
                <SummaryMetric
                  icon={VpnKeyOutlined}
                  label="Uso"
                  value="Activacion unica de cuenta"
                />
              </Stack>
            </Box>

            <Box
              sx={{
                px: { xs: 2.2, md: 4 },
                py: { xs: 2.5, md: 4 },
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              {loading ? (
                <Stack
                  spacing={1.25}
                  alignItems="center"
                  justifyContent="center"
                  sx={{ minHeight: 360, textAlign: "center" }}
                >
                  <CircularProgress size={24} />
                  <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
                    Verificando invitacion...
                  </Typography>
                </Stack>
              ) : inviteData?.valid ? (
                <Stack spacing={3}>
                  <Box>
                    <Box
                      sx={(currentTheme) => ({
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.75,
                        px: 1.1,
                        py: 0.6,
                        borderRadius: 999,
                        bgcolor: alpha(currentTheme.palette.success.main, 0.1),
                        color: currentTheme.palette.success.dark,
                      })}
                    >
                      <CheckCircleOutlineRounded sx={{ fontSize: 16 }} />
                      <Typography sx={{ fontSize: 11, fontWeight: 500 }}>
                        Invitacion validada
                      </Typography>
                    </Box>
                    <Typography sx={{ mt: 1.4, fontSize: { xs: 26, md: 34 }, fontWeight: 500, lineHeight: 1.12 }}>
                      Crea tu cuenta y deja listo tu acceso.
                    </Typography>
                    <Typography color="text.secondary" sx={{ mt: 1, fontSize: 14, lineHeight: 1.7, maxWidth: 560 }}>
                      Ingresa tu nombre, apellidos, DPI, usuario y contrasena. Tu acceso quedara
                      vinculado a la sucursal y rol definidos en esta invitacion.
                    </Typography>
                  </Box>

                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      bgcolor: alpha(theme.palette.primary.main, 0.025),
                    }}
                  >
                    <Stack spacing={1.75}>
                      <Box>
                        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                          Resumen de invitacion
                        </Typography>
                        <Typography sx={{ mt: 0.25, fontSize: 16, fontWeight: 500 }}>
                          Acceso inicial a {BRAND_NAME}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                          gap: 1.15,
                        }}
                      >
                        {summaryMetrics.map((item) => (
                          <SummaryMetric
                            key={item.label}
                            icon={item.icon}
                            label={item.label}
                            value={item.value}
                          />
                        ))}
                      </Box>

                      <Divider />

                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        {countdownParts.map((part) => (
                          <CountdownItem key={`summary-${part.label}`} label={part.label} value={part.value} />
                        ))}
                      </Stack>
                    </Stack>
                  </Paper>

                  <Box component="form" onSubmit={handleSubmit}>
                    <Stack spacing={1.5}>
                      {submitError ? (
                        <Alert severity="error" sx={{ fontSize: 12 }}>
                          {submitError}
                        </Alert>
                      ) : null}

                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                          gap: 1.4,
                        }}
                      >
                        <TextField
                          label="Nombre completo *"
                          size="small"
                          fullWidth
                          value={firstName}
                          onChange={(event) => setFirstName(event.target.value)}
                        />

                        <TextField
                          label="Apellidos *"
                          size="small"
                          fullWidth
                          value={lastName}
                          onChange={(event) => setLastName(event.target.value)}
                        />
                      </Box>

                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                          gap: 1.4,
                        }}
                      >
                        <TextField
                          label="DPI *"
                          size="small"
                          fullWidth
                          value={dpi}
                          onChange={(event) => setDpi(event.target.value)}
                          error={Boolean(dpi) && normalizedDpi.length !== 13}
                          helperText="Ingresa 13 digitos. Puedes pegarlo con o sin guiones."
                        />

                        <TextField
                          label="Nombre de usuario *"
                          size="small"
                          fullWidth
                          value={username}
                          onChange={(event) => setUsername(event.target.value)}
                          error={Boolean(username) && !usernamePatternValid}
                          helperText="Solo letras, numeros y puntos. Sin espacios."
                        />
                      </Box>

                      <Box>
                        <TextField
                          label="Contrasena *"
                          type="password"
                          size="small"
                          fullWidth
                          value={password}
                          onChange={(event) => setPassword(event.target.value)}
                        />
                        <Box
                          sx={(currentTheme) => ({
                            width: "100%",
                            height: 4,
                            borderRadius: 999,
                            bgcolor: currentTheme.palette.grey[300],
                            overflow: "hidden",
                            mt: 0.9,
                          })}
                        >
                          <Box
                            sx={{
                              width: `${passwordStrength}%`,
                              height: "100%",
                              background: getStrengthColor(passwordStrength),
                              transition: "width .2s, background .2s",
                            }}
                          />
                        </Box>
                      </Box>

                      <Box>
                        <TextField
                          label="Confirmar contrasena *"
                          type="password"
                          size="small"
                          fullWidth
                          value={confirmPassword}
                          onChange={(event) => setConfirmPassword(event.target.value)}
                        />
                        <FormHelperText
                          sx={{
                            color:
                              !confirmPassword
                                ? "text.secondary"
                                : passwordsMatch
                                  ? "#1D9E75"
                                  : "#E24B4A",
                          }}
                        >
                          {!confirmPassword
                            ? "Confirma la contrasena."
                            : passwordsMatch
                              ? "Las contrasenas coinciden"
                              : "Las contrasenas no coinciden"}
                        </FormHelperText>
                      </Box>

                      <Alert severity="info" sx={{ fontSize: 12 }}>
                        Este acceso es personal y de un solo uso. Una vez creada la cuenta, el
                        link dejara de estar disponible.
                      </Alert>

                      <Button
                        type="submit"
                        variant="contained"
                        disabled={!canSubmit}
                        sx={{ mt: 0.5, minHeight: 44 }}
                      >
                        {submitting ? "Creando cuenta..." : "Crear cuenta y acceder"}
                      </Button>
                    </Stack>
                  </Box>

                  <Typography sx={{ fontSize: 12, color: "text.secondary", textAlign: "center" }}>
                    Si necesitas ayuda, solicita al administrador un nuevo link o asistencia de
                    acceso.
                  </Typography>
                </Stack>
              ) : (
                <Stack spacing={2.2} sx={{ maxWidth: 520 }}>
                  <Box
                    sx={(currentTheme) => ({
                      width: 56,
                      height: 56,
                      borderRadius: "50%",
                      bgcolor: alpha(currentTheme.palette.error.main, 0.1),
                      color: currentTheme.palette.error.dark,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    })}
                  >
                    <AccessTimeOutlined sx={{ fontSize: 28 }} />
                  </Box>

                  <Box>
                    <Typography sx={{ fontSize: { xs: 24, md: 30 }, fontWeight: 500, lineHeight: 1.12 }}>
                      {invalidMeta.title}
                    </Typography>
                    <Typography color="text.secondary" sx={{ mt: 1, fontSize: 14, lineHeight: 1.7 }}>
                      {invalidMeta.description}
                    </Typography>
                  </Box>

                  {inviteData?.expires_at ? (
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1.7,
                        borderRadius: 2.5,
                        bgcolor: alpha(theme.palette.error.main, 0.025),
                      }}
                    >
                      <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                        Ultima expiracion registrada
                      </Typography>
                      <Typography sx={{ mt: 0.35, fontSize: 14, fontWeight: 500 }}>
                        {exactExpiry}
                      </Typography>
                    </Paper>
                  ) : null}

                  <Alert severity="info" sx={{ fontSize: 12 }}>
                    Solicita al administrador que genere un nuevo link de invitacion.
                  </Alert>

                  <Button
                    variant="outlined"
                    onClick={() => navigate("/login", { replace: true })}
                    sx={{ alignSelf: "flex-start", minHeight: 42 }}
                  >
                    Ir a iniciar sesion
                  </Button>
                </Stack>
              )}
            </Box>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}

export default InviteAcceptPage;
